import { BaseItem, Nullable, PersistentItem } from '../common';

import * as InsuranceProvider from '../insuranceProvider';
import * as PatientUpload from '../patientUpload';
import * as PrevisitPatientInsurance from '../previsit/patient/insurance';
import { InsuranceCardOcrResult } from '../insuranceOcrResult';

export enum PlanType {
  Hmo = 'HMO',
  Ppo = 'PPO',
  Epo = 'EPO',
  Pos = 'POS',
  Hdhp = 'HDHP',
  Hsa = 'HSA',
}

export enum EligibilityStatus {
  Active = 'ACTIVE',
  CannotProcess = 'CANNOT_PROCESS',
  Completed = 'COMPLETED',
  ContactOther = 'CONTACT_OTHER',
  Error = 'ERROR',
  Inactive = 'INACTIVE',
  Initiated = 'INITIATED',
  NonCovered = 'NON_COVERED',
  Pending = 'PENDING',
  PendingThirdPartyQueue = 'PENDING_THIRD_PARTY_QUEUE',
  Unknown = 'UNKNOWN',
}

export type BaseInsuranceInformation = {
  patientId: string;
  insuranceProviderId: string;
  primary: boolean;
  source: Source;
} & Nullable<{
  memberId: string;
  groupNumber: string;
  planType: PlanType;
  planName: string;
  ehrPlanId: string;
  eligibilityStatusCheck: EligibilityStatus;
  nameRaw: string;
  groupName: string;
  primaryCopay: number;
  specialtyCopay: number;
  payor: Payor | string;
  coverageStartDate: Date;
  coverageEndDate: Date;
  deletedTimestamp: Date;
  payorId: string;
  payorPlanId: string;
  eligibilitySubmitDate: Date;
}> &
  Partial<{
    planDescription: string;
    insuranceChanges: InsuranceInformationChanges;
  }>;

export type InsuranceInformationChanges = Nullable<{
  coverageStartDate: Date;
  coverageEndDate: Date;
  groupNumber: string;
  memberId: string;
  payor: Payor | string;
  planType: PlanType;
  planName: string;
  primaryPolicyHolderMemberId: string;
}>;

export interface InsuranceInformation
  extends BaseItem,
    BaseInsuranceInformation,
    PrevisitPatientInsurance.BaseSubscriber {}

export interface InsuranceInformationWithDetails
  extends InsuranceInformation,
    Nullable<PatientUpload.BaseUpload & PersistentItem>,
    InsuranceProvider.BaseInsuranceProvider,
    PrevisitPatientInsurance.BaseSubscriber {
  patientUploadId: string;
  ocrInformation?: InsuranceCardOcrResult;
  payorDisplayName?: string;
  completed?: boolean;
}

// Keep in sync with: backend/primary/src/models/patient/insuranceInformation.ts
// DEPRECATED: USE PAYOR TABLE INSTEAD
export enum Payor {
  Aarp = 'AARP',
  AarpGroupHealthcareOptions = 'AARP_GROUP_HEALTHCARE_OPTIONS',
  AarpMedicareAdvantageUnitedHealthcare = 'AARP_MEDICARE_ADVANTAGE_UNITED_HEALTHCARE',
  AarpUnitedHealthcare = 'AARP_UNITED_HEALTHCARE',
  Aetna = 'AETNA',
  AetnaBetterHealthKansas = 'AETNA_BETTER_HEALTH_KANSAS',
  AetnaMedicare = 'AETNA_MEDICARE',
  AetnaSeniorSupplemental = 'AETNA_SENIOR_SUPPLEMENTAL',
  AlliedBenefitSystems = 'ALLIED_BENEFIT_SYSTEMS',
  AmbetterFromSuperiousHealthPlanCommercial = 'AMBETTER_FROM_SUPERIOUS_HEALTH_PLAN_COMMERCIAL',
  AmericanFamily = 'AMERICAN_FAMILY',
  AmericanMedicalSecurity = 'AMERICAN_MEDICAL_SECURITY',
  AmericanRepublic = 'AMERICAN_REPUBLIC',
  AmeriHealth = 'AMERIHEALTH',
  AmeriHealthCaritas = 'AMERIHEALTH_CARITAS',
  AmeriHealthOfNewJersey = 'AMERIHEALTH_OF_NEW_JERSEY',
  AnthemBlueCrossBlueShield = 'ANTHEM_BLUE_CROSS_BLUE_SHIELD',
  ApwuHealthPlan = 'APWU_HEALTH_PLAN',
  AspireMedicare = 'ASPIRE_MEDICARE',
  AsurisNorthwestHealth = 'ASURIS_NORTHWEST_HEALTH',
  AutomatedBenefitServicesSmartHealth = 'AUTOMATED_BENEFIT_SERVICES_SMART_HEALTH',
  AutomatedHealthSystems = 'AUTOMATED_HEALTH_SYSTEMS',
  BankersLife = 'BANKERS_LIFE',
  BcbsBlueCrossAndBlueShield = 'BCBS_BLUE_CROSS_AND_BLUE_SHIELD',
  BcbsKansas = 'BCBS_KANSAS',
  BcbsKansasCity = 'BCBS_KANSAS_CITY',
  BeaconHealthOptions = 'BEACON_HEALTH_OPTIONS',
  BindBenefitsInc = 'BIND_BENEFITS_INC',
  BlueCross = 'BLUE_CROSS',
  BlueCrossBlueShieldofArizona = 'BLUE_CROSS_BLUE_SHIELD_OF_ARIZONA',
  BlueCrossBlueShieldofMinnesota = 'BLUE_CROSS_BLUE_SHIELD_OF_MINNESOTA',
  BlueCrossBlueShieldofMontana = 'BLUE_CROSS_BLUE_SHIELD_OF_MONTANA',
  BlueCrossBlueShieldofNorthCarolina = 'BLUE_CROSS_BLUE_SHIELD_OF_NORTH_CAROLINA',
  BlueCrossBlueShieldofNorthDakota = 'BLUE_CROSS_BLUE_SHIELD_OF_NORTH_DAKOTA',
  BlueCrossBlueShieldWellmark = 'BLUE_CROSS_BLUE_SHIELD_WELLMARK',
  BlueCrossFEP = 'BLUE_CROSS_FEP',
  BlueCrossMediCal = 'BLUE_CROSS_MEDI_CAL',
  BlueCrossMedicareAdvantage = 'BLUE_CROSS_MEDICARE_ADVANTAGE',
  BlueCrossNoMedicare = 'BLUE_CROSS_NO_MEDICARE',
  BlueEssentials = 'BLUE_ESSENTIALS',
  BlueShield = 'BLUE_SHIELD',
  BlueShieldMedicare = 'BLUE_SHIELD_MEDICARE',
  BridgeSpanHealth = 'BRIDGESPAN_HEALTH',
  CencalHealth = 'CENCAL_HEALTH',
  Centene = 'CENTENE',
  CentralCaliforniaAllianceForHealth = 'CENTRAL_CALIFORNIA_ALLIANCE_FOR_HEALTH',
  ChampVa = 'CHAMP_VA',
  Cigna = 'CIGNA',
  CignaApwu = 'CIGNA_APWU',
  CignaHealthSpring = 'CIGNA_HEALTH_SPRING',
  CignaNalc = 'CIGNA_NALC',
  Coastal = 'COASTAL',
  CoastalAlt = 'COASTAL_ALT',
  ColonialPennLife = 'COLONIAL_PENN_LIFE',
  CommercialInsuranceOther = 'COMMERCIAL_INSURANCE_OTHER',
  Coresource = 'CORESOURCE',
  CoresourceOfFmh = 'CORESOURCE_OF_FMH',
  Coventry = 'COVENTRY',
  Covid19HRSAUninsured = 'COVID19_HRSA_UNINSURED',
  DellChildrenChip = 'DELL_CHILDREN_CHIP',
  DellChildrenMedicaid = 'DELL_CHILDREN_MEDICAID',
  EMBS = 'EMBS',
  Emi = 'EMI',
  ExperienceHealth = 'EXPERIENCE_HEALTH',
  FederatedMutualInsurance = 'FEDERATED_MUTUAL_INSURANCE',
  Geha = 'GEHA',
  GoldenRule = 'GOLDEN_RULE',
  GreatAmerican = 'GREAT_AMERICAN',
  GreatWest = 'GREAT_WEST',
  GreatWestCigna = 'GREAT_WEST_CIGNA',
  GroupAndPensionAdministrators = 'GROUP_AND_PENSION_ADMINISTRATORS',
  GroupHealthCoopEauClaire = 'GROUP_HEALTH_COOP_EAU_CLAIRE',
  HealtheAtCerner = 'HEALTHE_AT_CERNER',
  HealthNet = 'HEALTHNET',
  HealthNetPrisoner = 'HEALTHNET_PRISONER',
  HealthPartners = 'HEALTH_PARTNERS',
  HealthyBlueNorthCarolina = 'HEALTHY_BLUE_NORTH_CAROLINA',
  HMCAPS = 'HMC_APS',
  HorizonBlueCrossBlueShield = 'HORIZON_BLUE_CROSS_BLUE_SHIELD',
  HorizonNJHealth = 'HORIZON_NJ_HEALTH',
  Humana = 'HUMANA',
  HumanaMedicareAdvantage = 'HUMANA_MEDICARE_ADVANTAGE',
  HumanaMedicareAdvantageSupplement = 'HUMANA_MEDICARE_ADVANTAGE_SUPPLEMENT',
  IlliniCareHealth = 'ILLINICARE_HEALTH',
  IllinoisMedicaid = 'ILLINOIS_MEDICAID',
  IndependenceBlueCross = 'INDEPENDENCE_BLUE_CROSS',
  IndependenceBlueCrossBlueShield = 'INDEPENDENCE_BLUE_CROSS_BLUE_SHIELD',
  KaiserMedicare = 'KAISER_MEDICARE',
  LifeWise = 'LIFEWISE',
  LouisianaHealthcareConnections = 'LOUISIANA_HEALTHCARE_CONNECTIONS',
  LoyalAmerican = 'LOYAL_AMERICAN',
  MagellanHealthServices = 'MAGELLAN_HEALTH_SERVICES',
  MailHandlers = 'MAIL_HANDLERS',
  MaricopaCareAdvantage = 'MARICOPA_CARE_ADVANTAGE',
  Medicaid = 'MEDICAID',
  MedicaidAmerigroup = 'MEDICAID_AMERIGROUP',
  MedicaidBcbsStar = 'MEDICAID_BCBS_STAR',
  MedicaidKansas = 'MEDICAID_KANSAS',
  MedicaidMissouri = 'MEDICAID_MISSOURI',
  MedicaidSuperiorHealthPlan = 'MEDICAID_SUPERIOR_HEALTH_PLAN',
  MedicaidTmhp = 'MEDICAID_TMHP',
  Medica = 'MEDICA',
  MediCal = 'MEDI_CAL',
  Medicare = 'MEDICARE',
  MedicareReplacement = 'MEDICARE_REPLACEMENT',
  Mediview = 'MEDIVIEW',
  MeridianHealthPlan = 'MERIDIAN_HEALTH_PLAN',
  MeritainHealth = 'MERITAIN_HEALTH',
  MHN = 'MHN',
  MutualOfOmaha = 'MUTUAL_OF_OMAHA',
  NALC = 'NALC',
  Neighborhood = 'NEIGHBORHOOD',
  SanfordHealthPlan = 'SANFORD_HEALTH_PLAN',
  SanfordHealthPlanNorthDakotaPublicEmployeeRetirementSystem = 'SANFORD_HEALTH_PLAN_NORTH_DAKOTA_PUBLIC_EMPLOYEE_RETIREMENT_SYSTEM',
  OptumVA = 'OPTUM_VA',
  Oscar = 'OSCAR',
  Other = 'OTHER',
  OxfordHealth = 'OXFORD_HEALTH',
  Pehp = 'PEHP',
  Phcs = 'PHCS',
  PhysiciansMutual = 'PHYSICIANS_MUTUAL',
  PlannedAdministratorsInc = 'PLANNED_ADMINISTRATORS_INC',
  PreferredOne = 'PREFERRED_ONE',
  PremeraBlueCrossWA = 'PREMERA_BLUE_CROSS_WA',
  PremeraBlueCrossBlueShield = 'PREMERA_BLUE_CROSS_BLUE_SHIELD',
  ProvidenceHealthPlans = 'PROVIDENCE_HEALTH_PLANS',
  QuickTrip = 'QUICK_TRIP',
  RegenceBlueCross = 'REGENCE_BLUE_CROSS',
  RegenceBlueCrossBlueShield = 'REGENCE_BLUE_CROSS_BLUE_SHIELD',
  RegenceBlueShieldWA = 'REGENCE_BLUE_SHIELD_WASHINGTON',
  SecurityAdministrativeServices = 'SECURITY_ADMINISTRATIVE_SERVICES',
  SecurityHealthPlan = 'SECURITY_HEALTH_PLAN',
  SelectHealth = 'SELECT_HEALTH',
  SenderoIdealcare = 'SENDERO_IDEALCARE',
  StandardLife = 'STANDARD_LIFE',
  StateFarm = 'STATE_FARM',
  SunflowerHealth = 'SUNFLOWER_HEALTH',
  TransAmerica = 'TRANS_AMERICA',
  Tricare = 'TRICARE',
  TricareWest = 'TRICARE_WEST',
  TricareForLife = 'TRICARE_FOR_LIFE',
  Triwest = 'TRIWEST',
  UhcCommunityMedicaidEvercare = 'UHC_COMMUNITY_MEDICAID_EVERCARE',
  UhcGoldenRule = 'UHC_GOLDEN_RULE',
  UhcMedica = 'UHC_MEDICA',
  Umr = 'UMR',
  UnionPacificRrInsuranceCo = 'UNION_PACIFIC_RR_INSURANCE_CO',
  UnitedAmerican = 'UNITED_AMERICAN',
  UnitedBehavioral = 'UNITED_BEHAVIORAL',
  UnitedHealthcare = 'UNITED_HEALTHCARE',
  UnitedHealthcareCommunityPlanKansas = 'UNITED_HEALTHCARE_COMMUNITY_PLAN_KANSAS',
  UnitedHealthcareMedicare = 'UNITED_HEALTHCARE_MEDICARE',
  UnitedHealthcareStudentResources = 'UNITED_HEALTHCARE_STUDENT_RESOURCES',
  UsaaLifeInsuranceCo = 'USAA_LIFE_INSURANCE_CO',
  UtahMedicaid = 'UTAH_MEDICAID',
  VeteransAdministration = 'VETERANS_ADMINISTRATION',
  WebTpaInc = 'WEB_TPA_INC',
  WellcareMedicareAdvantage = 'WELLCARE_MEDICARE_ADVANTAGE',
  WesternGrowers = 'WESTERN_GROWERS',
  WellCareByAllwell = 'WELLCARE_BY_ALLWELL',
}

export enum HealthPlansToExport {
  Aetna = 'AETNA',
  AetnaAdv = 'AETNA_ADV',
  AetnaMcd = 'AETNA_MCD',
  AetnaSNP = 'AETNA_SNP',
  AetnaUSHealthcare = 'AETNA_US_HEALTHCARE',
  AetnaWLU = 'AETNA_WLU',
  BcBlueAccess = 'BC_BLUE_ACCESS',
  BcBlueCare = 'BC_BLUE_CARE',
  BcBlueExchange = 'BC_BLUE_EXCHANGE',
  BcBlueMedicareAdv = 'BC_BLUE_MEDICARE_ADV',
  BcBlueSelect = 'BC_BLUE_SELECT',
  BcBlueSelectExchange = 'BC_BLUE_SELECT_EXCHANGE',
  BcBlueSelectPlus = 'BC_BLUE_SELECT_PLUS',
  BcBlueSelectPlusExchange = 'BC_BLUE_SELECT_PLUS_EXCHANGE',
  BcCityofKCMO = 'BC_CITY_OF_KCMO',
  BcExchng = 'BC_EXCHNG',
  BcFedPlanPrefCare = 'BC_FED_PLAN_PREF_CARE',
  BcHealthkeepers = 'BC_HEALTHKEEPERS',
  BcHighPerformanceNetwork = 'BC_HIGH_PERFORMANCE_NETWORK',
  BcMedicareSupplement = 'BC_MEDICARE_SUPPLEMENT',
  BcNKCH = 'BC_NKCH',
  BcOOAMedicareAdv = 'BC_OOA_MEDICARE_ADV',
  BcOOAPrefCare = 'BC_OOA_PREF_CARE',
  BcOOAPrefCareBlue = 'BC_OOA_PREF_CARE_BLUE',
  BcPrefCare = 'BC_PREF_CARE',
  BcPrefCareBlue = 'BC_PREF_CARE_BLUE',
  BcPreferredCareBlueExchange = 'BC_PREFERRED_CARE_BLUE_EXCHANGE',
  BcVa = 'BC_VA',
  BcVaMcra = 'BC_VA_MCRA',
  BcVaPlus = 'BC_VA_PLUS',
  BlueCrossBlueShieldHMO = 'BLUE_CROSS_BLUE_SHIELD_HMO',
  BlueCrossBlueShieldPPO = 'BLUE_CROSS_BLUE_SHIELD_PPO',
  CCCAetna = 'CCC_AETNA',
  CCCAnthem = 'CCC_ANTHEM',
  Cigna = 'CIGNA',
  MedexMIT = 'MEDEX_MIT',
  MedicarePartA = 'MEDICARE_PART_A',
  MedicarePartAandB = 'MEDICARE_PART_A_AND_B',
  MedicarePartB = 'MEDICARE_PART_B',
  MedicareRailroad = 'MEDICARE_RAILROAD',
  MeritainAugusta = 'MERIT_AUG',
  MITHighDeductiblePlan = 'MIT_HIGH_DEDUCTIBLE_PLAN',
  MITStudentHealthInsurancePlanAffiliate = 'MIT_STUDENT_HEALTH_INSURANCE_PLAN_AFFILIATE',
  MITStudentHealthInsurancePlanStudent = 'MIT_STUDENT_HEALTH_INSURANCE_PLAN_STUDENT',
  MITStudentHealthInsurancePlanVisitingStudent = 'MIT_STUDENT_HEALTH_INSURANCE_PLAN_VISITING_STUDENT',
  MITTraditionalPlan = 'MIT_TRADITIONAL_PLAN',
  UnitedHealthcare = 'UNITED_HEALTHCARE',
}

export const PAYORS_NO_GROUP_NUMBER = [
  // TODO: DEPRECATED -  ADD TO PAYORS TABLE AND REMOVE
  Payor.Oscar,
  Payor.AmbetterFromSuperiousHealthPlanCommercial,
  Payor.Medicare,
  Payor.Medicaid,
  Payor.MedicaidAmerigroup,
  Payor.MedicaidBcbsStar,
  Payor.MedicaidMissouri,
  Payor.MedicaidSuperiorHealthPlan,
  Payor.MedicaidTmhp,
];

export interface InsuranceCardInformation
  extends Pick<
      InsuranceInformation,
      | 'id'
      | 'payor'
      | 'groupNumber'
      | 'coverageStartDate'
      | 'planDescription'
      | 'insuranceChanges'
      | 'primary'
    >,
    Pick<
      PrevisitPatientInsurance.BaseSubscriber,
      'primaryPolicyHolderMemberId'
    > {}

export interface PrevisitPatientInsuranceInformation
  extends PrevisitPatientInsurance.PrevisitSubscriber,
    PrevisitPatientInsurance.InsuranceCoverageWithUpdates {}

export interface InsuranceInformationUpdates {
  extractedPatientInsuranceCoverages: InsuranceInformationWithDetails[];
  insuranceCoverages: PrevisitPatientInsuranceInformation[];
  payingWithoutInsurance: boolean;
}

export interface TerminatedInsurance {
  payor: Payor | string;
  primaryPolicyHolderMemberId: string;
}

export enum Source {
  Patient = 'PATIENT',
  Ehr = 'EHR',
}

export type FindAllOptions = Partial<{
  limit?: number;
  encounterId?: string;
  source?: Source;
}>;

export interface PredictedPlanResult {
  ehrPlanId?: string;
  ehrPlanName?: string;
}
